import { PrivateRoute, useStreamValue } from 'annotation-tools/utils';
import React, { useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import { AppBar } from './components/app-bar/app-bar';
import { Home } from './components/pages/home';
import { Login } from './components/pages/login';
import { NotFoundPage } from './components/pages/not-found';
import { LoadingPage } from './components/shared/loading-page';
import { colorDef } from './constants/styles/color-def';
import { useLoggedIn } from './global-state/auth-state';
import { loadingPageIsOpen$, closeLoadingPage } from './global-state/show-loading-page';
import { PathnameListener } from './routing/pathname-listener';
import { privateRoutes } from './routing/private-routes';
import { redirectRules } from './routing/redirect-rules';
import { routes } from './routing/routes';

const Root = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const AppbarWrapper = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
`;

const Body = styled.div`
  background-color: ${colorDef.bg};
  flex-grow: 1;
  flex-shrink: 1;
  /* styles for children */
  display: flex;
  flex-direction: column;
`;

const Main = styled.div`
  width: 100%;
  height: 100%;
  flex-grow: 1;
  flex-shrink: 1;
  /* styles for children */
  display: flex;
  flex-direction: row;
  align-items: stretch;
`;

const App: React.FC = () => {
  const loggedIn = useLoggedIn();

  const loadingPageIsOpen = useStreamValue(loadingPageIsOpen$, false);
  
  // グローバルローディングタイムアウトを追加（スタック防止）
  useEffect(() => {
    let timeout: any;
    
    if (loadingPageIsOpen) {
      // 15秒後に自動的にローディングを閉じる
      timeout = setTimeout(() => {
        console.warn('グローバルローディングタイムアウト - 強制終了');
        // 複数のクローズイベントで強制的に閉じる
        for (let i = 0; i < 20; i++) {
          closeLoadingPage();
        }
      }, 15000);
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [loadingPageIsOpen]);

  return (
    <BrowserRouter>
      <PathnameListener />
      <Root>
        <LoadingPage isOpen={loadingPageIsOpen} />
        <AppbarWrapper>
          <AppBar />
        </AppbarWrapper>
        <Body>
          <Main>
            <Switch>
              <Route exact={true} path={routes.home} component={Home} />
              <Route exact={true} path={routes.login} component={Login} />
              {privateRoutes.map(([route, component]) => (
                <PrivateRoute
                  key={route}
                  loggedIn={loggedIn}
                  exact={true}
                  path={route}
                  redirectTo={routes.login}
                  component={component}
                />
              ))}
              {redirectRules.map(({ from, to }) => (
                <Route
                  key={from}
                  path={from}
                  render={({ match }) => <Redirect to={to(match as any)} />}
                  exact={true}
                />
              ))}
              <Route path='*' component={NotFoundPage} />
            </Switch>
          </Main>
        </Body>
      </Root>
    </BrowserRouter>
  );
};

export default App;
